/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { OperationDelayStrategies } from 'src/core/utils/api/operationDelayStrategies';
import { getCustomTextTestModelService } from 'src/customText/core/services/testModels/customTextTestModelService';
import { CustomTextTestModelResults } from 'src/customText/core/services/testModels/types';

export type CustomTextTestModelHandlerGroup = ReturnType<typeof createCustomTextTestModelHandlerGroup>;

/**
 * Represents a handler group for custom text model test.
 *
 * @param dependencies The handler group dependencies.
 */
export const createCustomTextTestModelHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { rootStore } = dependencies;

    const testCustomTextModel = async ({
        document,
        language,
        deploymentName,
        setTestResults
    }: HandlerParams<{
        document: string;
        language?: string;
        deploymentName: string;
        setTestResults: (testResults: CustomTextTestModelResults) => void;
    }>) => {
        setTestResults(undefined);

        const { name: projectName, deploymentsStoreHouse: deployments } = rootStore.currentCustomTextProjectStore;
        const deployment = deployments.findById(deploymentName);

        if (!deployment) {
            return;
        }

        const testResult = await getCustomTextTestModelService().testModel({
            document,
            language,
            projectName,
            deploymentName,
            projectKind: rootStore.currentCustomTextProjectStore.projectKind,
            serviceKey: rootStore.azureStore.session.serviceKey,
            operationHandle: {
                timeToDelay: OperationDelayStrategies.exponentialDelayStrategy,
                retryStrategy: res => res.data.status !== 'succeeded' && res.data.status !== 'failed'
            }
        });

        if (testResult.status === 'succeeded') {
            setTestResults({
                entities: testResult.tasks.items?.[0].results.documents[0].entities,
                classes: testResult.tasks.items?.[0].results.documents[0].class || testResult.tasks.items?.[0].results.documents[0].classes
            });
        }
    };

    return { testCustomTextModel };
};
